// 스페셜 몬스터 VFX(STAGE-WORLD-PRD 4절): 황금 고블린 = 발밑 금빛 오라 + 달리는 길에 남는 반짝이 궤적 + 처치 때 금화 터짐 + 땅속으로 사라질 때 먼지,
// 챔피언 = 접두어 색 오라 고리 + 천천히 오르는 불씨 + 머리 위 이름표(DOM). 가산 혼합, 점멸 없음(맥동은 0.4Hz 사인, 섬광 1초 3회 규칙 안).
// 드로우콜 +3: 오라 InstancedMesh 1(바닥에 눕는 원판) + 반짝이 InstancedMesh 1(카메라 정면 점, 황금 고블린 머리 위 금빛 기둥도 이 메시의 인스턴스 1개) + 금 자루 Mesh 1(황금 고블린만). 이름표는 DOM이라 드로우콜이 없다.
// 풀은 미리 할당하고 CPU로 갱신한다(프레임 루프 새 객체 없음). 화면 흔들림·섬광 없음(FlashLimiter 예산을 쓰지 않는다).
// 예산(품질 particleScale 1 기준, 0.5 단계에서 절반): 오라 ≤ 4판, 반짝이 ≤ 72개 살아 있음, 궤적 초당 ≤ 26개, 처치 금화 터짐 1회 = 반짝이 18개, 등장 고리 = 10개, 소멸 먼지 = 10개.
import * as THREE from 'three/webgpu';
import { attribute, float, length, mix, normalWorld, smoothstep, uv, vec2, vec3 } from 'three/tsl';
import { Ev, type GameEvent } from '../shared/events';
import { SPECIAL } from '../shared/content/special';
import { CHAMPION_AFFIXES, MobSpecial, SPECIAL_RT, SpecialEv } from '../shared/specialMonster';
import { MobState, type GameView } from '../shared/types';
import { TARGET_H } from '../render/mobs';
import { TagPosCache } from './tagCache';

/** 효과별 상한(품질 배율 1). 테스트·문서가 같은 값을 읽는다 */
export const SPECIAL_FX_BUDGET = {
  aura: 4,
  spark: 72,
  trailPerSec: 26,
  championPerSec: 10,
  rewardBurst: 18,
  appearRing: 10,
  vanishDust: 10,
} as const;

const GOLD = 0xffb81c;
const PALE = 0xfff1b8;

export class SpecialFx {
  readonly group = new THREE.Group();
  private readonly auraMesh: THREE.InstancedMesh;
  private readonly auraAlpha: THREE.InstancedBufferAttribute;
  private readonly sparkMesh: THREE.InstancedMesh;
  private readonly sparkAlpha: THREE.InstancedBufferAttribute;
  private readonly cap: number;
  /** 황금 고블린 등 뒤 금 자루(구 메시 1개) */
  private readonly sack: THREE.Mesh;
  // 반짝이 풀(구조체 배열 대신 평행 배열)
  private n = 0;
  private readonly px: Float32Array; private readonly py: Float32Array; private readonly pz: Float32Array;
  private readonly vx: Float32Array; private readonly vy: Float32Array; private readonly vz: Float32Array;
  private readonly age: Float32Array; private readonly life: Float32Array; private readonly w0: Float32Array; private readonly w1: Float32Array;
  private readonly cr: Float32Array; private readonly cg: Float32Array; private readonly cb: Float32Array; private readonly a0: Float32Array;
  private readonly grav: Float32Array; private readonly drag: Float32Array;
  private seed = 0x9e3779b1;
  private trailAcc = 0;
  private champAcc = 0;
  private reduceFlash = false;
  private readonly right = new THREE.Vector3();
  private readonly up = new THREE.Vector3();
  private readonly fwd = new THREE.Vector3();
  private readonly proj = new THREE.Vector3();
  /** 챔피언 이름표(DOM, 머리 위) */
  private readonly tag: HTMLDivElement;
  private tagText = '';
  private tagOn = false;
  private readonly tagPos = new TagPosCache();
  /** QA: 누적 생성 수와 이번 프레임에 그린 오라 수 */
  readonly spawned = { trail: 0, burst: 0, dust: 0, ring: 0 };
  auraDrawn = 0;

  constructor(particleScale: number, fxLayer: HTMLElement) {
    const scale = Math.max(0.25, particleScale);
    this.cap = Math.max(16, Math.floor(SPECIAL_FX_BUDGET.spark * scale));
    // 오라: 바닥에 눕는 원판(중심은 옅고 가장자리 고리가 밝다)
    const ag = new THREE.PlaneGeometry(1, 1);
    this.auraAlpha = new THREE.InstancedBufferAttribute(new Float32Array(SPECIAL_FX_BUDGET.aura), 1);
    this.auraAlpha.setUsage(THREE.DynamicDrawUsage);
    ag.setAttribute('aAlpha', this.auraAlpha);
    const am = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending });
    am.forceSinglePass = true; am.fog = false;
    const p = uv().sub(0.5).mul(2);
    const d = length(vec2(p.x, p.y));
    const glow = smoothstep(float(1), float(0.0), d).pow(1.8).mul(0.45);
    const ring = smoothstep(float(0.18), float(0.0), d.sub(0.82).abs()).mul(0.9);
    am.opacityNode = glow.add(ring).mul(smoothstep(float(1.0), float(0.92), d)).mul(attribute('aAlpha', 'float'));
    this.auraMesh = new THREE.InstancedMesh(ag, am, SPECIAL_FX_BUDGET.aura);
    this.auraMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.auraMesh.setColorAt(0, new THREE.Color(1, 1, 1));
    (this.auraMesh.instanceColor as THREE.InstancedBufferAttribute).setUsage(THREE.DynamicDrawUsage);
    this.auraMesh.frustumCulled = false; this.auraMesh.count = 0; this.auraMesh.visible = false; this.auraMesh.renderOrder = 18;
    // 반짝이: 가산 점
    const sg = new THREE.PlaneGeometry(1, 1);
    this.sparkAlpha = new THREE.InstancedBufferAttribute(new Float32Array(this.cap + 1), 1);
    this.sparkAlpha.setUsage(THREE.DynamicDrawUsage);
    sg.setAttribute('aAlpha', this.sparkAlpha);
    const sm = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending });
    sm.forceSinglePass = true; sm.fog = false;
    const q = uv().sub(0.5).mul(2);
    const sd = length(vec2(q.x, q.y));
    sm.opacityNode = smoothstep(float(1), float(0.0), sd).pow(1.6).mul(attribute('aAlpha', 'float'));
    // +1: 머리 위 금빛 기둥(풀 밖 고정 인스턴스)
    this.sparkMesh = new THREE.InstancedMesh(sg, sm, this.cap + 1);
    this.sparkMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.sparkMesh.setColorAt(0, new THREE.Color(1, 1, 1));
    (this.sparkMesh.instanceColor as THREE.InstancedBufferAttribute).setUsage(THREE.DynamicDrawUsage);
    this.sparkMesh.frustumCulled = false; this.sparkMesh.count = 0; this.sparkMesh.visible = false; this.sparkMesh.renderOrder = 23;
    // 금 자루: 금색 구(위쪽이 밝은 단순 음영). 본에 붙이지 않고 몸 뒤를 따라간다
    const sackMat = new THREE.MeshBasicNodeMaterial();
    sackMat.fog = false;
    sackMat.colorNode = mix(vec3(0.4, 0.22, 0.03), vec3(1.0, 0.72, 0.14), normalWorld.y.mul(0.5).add(0.5).pow(1.6));
    this.sack = new THREE.Mesh(new THREE.SphereGeometry(0.4, 14, 10), sackMat);
    this.sack.frustumCulled = false; this.sack.visible = false; this.sack.renderOrder = 5;
    this.group.add(this.auraMesh, this.sparkMesh, this.sack);
    const f = (): Float32Array => new Float32Array(this.cap);
    this.px = f(); this.py = f(); this.pz = f(); this.vx = f(); this.vy = f(); this.vz = f();
    this.age = f(); this.life = f(); this.w0 = f(); this.w1 = f();
    this.cr = f(); this.cg = f(); this.cb = f(); this.a0 = f(); this.grav = f(); this.drag = f();
    // 이름표: 위쪽 가운데 기준, 몹 머리 위. 플레이를 막지 않게 pointer-events 없음
    this.tag = document.createElement('div');
    this.tag.style.cssText = 'position:absolute;left:0;top:0;display:none;pointer-events:none;white-space:nowrap;padding:1px 9px 2px;border-radius:9px;' +
      'font:900 13px system-ui,"Malgun Gothic","Apple SD Gothic Neo",sans-serif;letter-spacing:1px;color:#ffe9a6;' +
      'background:linear-gradient(180deg,rgba(40,28,6,.82),rgba(14,10,4,.82));border:1.5px solid #f2c14e;text-shadow:0 1px 3px rgba(0,0,0,.9);box-shadow:0 0 8px rgba(242,193,78,.45);will-change:transform;';
    this.tag.dataset.testid = 'special-nametag';
    fxLayer.appendChild(this.tag);
  }

  private rnd(): number {
    this.seed = (Math.imul(this.seed, 1664525) + 1013904223) >>> 0;
    return this.seed / 4294967296;
  }

  setReduceFlash(on: boolean): void { this.reduceFlash = on; }

  /** 살아 있는 반짝이 수(QA·예산 테스트) */
  get active(): number { return this.n; }

  private spawn(x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, w0: number, w1: number, color: number, alpha: number, grav: number, drag: number): void {
    if (this.n >= this.cap) return;
    const i = this.n++;
    this.px[i] = x; this.py[i] = y; this.pz[i] = z; this.vx[i] = vx; this.vy[i] = vy; this.vz[i] = vz;
    this.age[i] = 0; this.life[i] = life; this.w0[i] = w0; this.w1[i] = w1;
    this.cr[i] = (color >> 16 & 255) / 255; this.cg[i] = (color >> 8 & 255) / 255; this.cb[i] = (color & 255) / 255;
    this.a0[i] = alpha * (this.reduceFlash ? 0.55 : 1); this.grav[i] = grav; this.drag[i] = drag;
  }

  private kill(i: number): void {
    const j = --this.n;
    if (i === j) return;
    this.px[i] = this.px[j]; this.py[i] = this.py[j]; this.pz[i] = this.pz[j]; this.vx[i] = this.vx[j]; this.vy[i] = this.vy[j]; this.vz[i] = this.vz[j];
    this.age[i] = this.age[j]; this.life[i] = this.life[j]; this.w0[i] = this.w0[j]; this.w1[i] = this.w1[j];
    this.cr[i] = this.cr[j]; this.cg[i] = this.cg[j]; this.cb[i] = this.cb[j]; this.a0[i] = this.a0[j]; this.grav[i] = this.grav[j]; this.drag[i] = this.drag[j];
  }

  /** 스페셜 이벤트(Appear·Reward·Vanish) */
  onEvent(e: GameEvent): void {
    if (e.type === SpecialEv.Reward) {
      // 금화 터짐: 위로 튀어 중력으로 떨어지는 금빛 점(바닥에서 한 번 튄다)
      for (let i = 0; i < SPECIAL_FX_BUDGET.rewardBurst; i++) {
        const a = this.rnd() * Math.PI * 2, s = 1.2 + this.rnd() * 2.6;
        this.spawn(e.x, 0.6 + this.rnd() * 0.4, e.z, Math.sin(a) * s, 3.2 + this.rnd() * 2.4, Math.cos(a) * s, 0.8 + this.rnd() * 0.5, 0.2, 0.08, this.rnd() < 0.7 ? GOLD : PALE, 0.95, 11, 0.8);
      }
      this.spawned.burst++;
    } else if (e.type === SpecialEv.Vanish) {
      for (let i = 0; i < SPECIAL_FX_BUDGET.vanishDust; i++) {
        const a = this.rnd() * Math.PI * 2, s = 0.6 + this.rnd() * 1.0;
        this.spawn(e.x + Math.sin(a) * 0.3, 0.12, e.z + Math.cos(a) * 0.3, Math.sin(a) * s, 0.5 + this.rnd() * 0.7, Math.cos(a) * s, 0.7 + this.rnd() * 0.4, 0.35, 0.7, 0xcdb56a, 0.45, 0.3, 1.6);
      }
      this.spawned.dust++;
    } else if (e.type === SpecialEv.Appear) {
      // 등장 고리: 발밑에서 사방으로 번지는 금빛 점
      const col = e.a === MobSpecial.Champion ? CHAMPION_AFFIXES[e.c | 0]?.color ?? GOLD : GOLD;
      for (let i = 0; i < SPECIAL_FX_BUDGET.appearRing; i++) {
        const a = (i / SPECIAL_FX_BUDGET.appearRing) * Math.PI * 2;
        this.spawn(e.x, 0.15, e.z, Math.sin(a) * 2.2, 0.4, Math.cos(a) * 2.2, 0.7, 0.18, 0.06, col, 0.9, 0, 2.2);
      }
      this.spawned.ring++;
    } else if (e.type === Ev.SceneChange) this.clear();
  }

  /**
   * 매 프레임. dt = 세계 시계 초(일시정지·히트스톱 0). 오라·궤적·이름표는 이 판의 스페셜 몹(view.special.mob)만.
   * width/height: 이름표 투영용 화면 크기(px)
   */
  update(view: GameView, dt: number, camera: THREE.Camera, time: number, width: number, height: number): void {
    const sp = view.special, m = view.mobs;
    const mob = sp && sp.phase === 1 ? sp.mob : -1;
    const alive = mob >= 0 && m.state[mob] !== MobState.Inactive && m.special !== undefined && m.special[mob] !== 0;
    camera.matrixWorld.extractBasis(this.right, this.up, this.fwd);
    const am = this.auraMesh.instanceMatrix.array as Float32Array, ac = this.auraMesh.instanceColor!.array as Float32Array, aa = this.auraAlpha.array as Float32Array;
    let ac2 = 0;
    let pillar = false, sackOn = false;
    // 오라는 지금 출현 중인 스페셜 몹(Dying 포함 — 땅속으로 가라앉는 동안도 따라간다)
    const showAura = mob >= 0 && alive;
    if (showAura) {
      const champ = m.special![mob] === MobSpecial.Champion;
      const aff = champ ? CHAMPION_AFFIXES[m.affix![mob]] : null;
      const color = aff ? aff.color : GOLD;
      const r = (champ ? SPECIAL.champion.scale * 1.15 : 0.95 * SPECIAL_RT.goldScale) * (this.reduceFlash ? 0.92 : 1);
      const pulse = 0.82 + 0.12 * Math.sin(time * Math.PI * 0.8);
      const dying = m.state[mob] === MobState.Dying;
      const x = m.x[mob], z = m.z[mob], y = dying ? -0.5 : 0.04;
      const w = r * 2.1;
      const o = ac2 * 16;
      am[o] = w; am[o + 1] = 0; am[o + 2] = 0; am[o + 3] = 0;
      am[o + 4] = 0; am[o + 5] = 0; am[o + 6] = -w; am[o + 7] = 0;
      am[o + 8] = 0; am[o + 9] = 1; am[o + 10] = 0; am[o + 11] = 0;
      am[o + 12] = x; am[o + 13] = y; am[o + 14] = z; am[o + 15] = 1;
      ac[ac2 * 3] = (color >> 16 & 255) / 255; ac[ac2 * 3 + 1] = (color >> 8 & 255) / 255; ac[ac2 * 3 + 2] = (color & 255) / 255;
      aa[ac2] = (dying ? 0 : 0.6 * pulse) * (this.reduceFlash ? 0.6 : 1);
      ac2++;
      if (!champ) {
        // 금 자루: 몸 뒤(바라보는 반대쪽)·등 높이. 사라질 때(Dying)는 몸과 같이 가라앉는다
        const sc = SPECIAL_RT.goldScale, f = m.facing[mob], back = 0.5 * sc;
        const sink = dying ? Math.min(1, m.stateTick[mob] / 40) * 2.2 : 0;
        this.sack.position.set(m.x[mob] - Math.sin(f) * back, 1.25 * sc - sink, m.z[mob] - Math.cos(f) * back);
        const bob = 1 + 0.03 * Math.sin(time * 5);
        this.sack.scale.set(0.85 * sc * bob, 0.95 * sc * bob, 0.8 * sc * bob);
        this.sack.rotation.y = f;
        sackOn = m.state[mob] !== MobState.Spawning;
        pillar = !dying;
      }
      // 궤적·불씨: 황금 고블린은 달리는 동안 반짝이를 길에 남기고, 챔피언은 몸 둘레에서 천천히 오른다
      if (dt > 0 && !dying) {
        if (!champ) {
          const moving = Math.hypot(m.x[mob] - m.prevX[mob], m.z[mob] - m.prevZ[mob]) > 0.004;
          this.trailAcc = Math.min(3, this.trailAcc + dt * SPECIAL_FX_BUDGET.trailPerSec * (moving ? 1 : 0.35));
          while (this.trailAcc >= 1) {
            this.trailAcc -= 1;
            const a = this.rnd() * Math.PI * 2, rr = 0.15 + this.rnd() * 0.3;
            this.spawn(x + Math.sin(a) * rr, 0.25 + this.rnd() * 0.9, z + Math.cos(a) * rr, 0, 0.35 + this.rnd() * 0.3, 0, 0.5 + this.rnd() * 0.3, 0.17, 0.04, this.rnd() < 0.65 ? GOLD : PALE, 0.9, 0, 1.2);
            this.spawned.trail++;
          }
        } else {
          this.champAcc = Math.min(3, this.champAcc + dt * SPECIAL_FX_BUDGET.championPerSec);
          while (this.champAcc >= 1) {
            this.champAcc -= 1;
            const a = this.rnd() * Math.PI * 2, rr = 0.5 + this.rnd() * 0.7;
            this.spawn(x + Math.sin(a) * rr, 0.1, z + Math.cos(a) * rr, 0, 0.7 + this.rnd() * 0.5, 0, 0.9 + this.rnd() * 0.5, 0.14, 0.04, color, 0.8, 0, 0.4);
            this.spawned.trail++;
          }
        }
      }
    }
    this.sack.visible = sackOn;
    this.auraDrawn = ac2;
    this.auraMesh.count = ac2; this.auraMesh.visible = ac2 > 0;
    this.auraMesh.instanceMatrix.needsUpdate = true; this.auraMesh.instanceColor!.needsUpdate = true; this.auraAlpha.needsUpdate = true;
    // 반짝이 적분·빌보드
    const r = this.right, u = this.up, f = this.fwd;
    const sm = this.sparkMesh.instanceMatrix.array as Float32Array, sc = this.sparkMesh.instanceColor!.array as Float32Array, sa = this.sparkAlpha.array as Float32Array;
    let i = 0;
    while (i < this.n) {
      if (dt > 0) {
        this.age[i] += dt;
        if (this.age[i] >= this.life[i]) { this.kill(i); continue; }
        const k = Math.max(0, 1 - this.drag[i] * dt);
        this.vy[i] -= this.grav[i] * dt;
        this.vx[i] *= k; this.vz[i] *= k;
        this.px[i] += this.vx[i] * dt; this.py[i] += this.vy[i] * dt; this.pz[i] += this.vz[i] * dt;
        if (this.py[i] < 0.03) { this.py[i] = 0.03; this.vy[i] *= -0.35; this.vx[i] *= 0.6; this.vz[i] *= 0.6; }
      }
      const t = this.age[i] / this.life[i];
      const w = this.w0[i] + (this.w1[i] - this.w0[i]) * t;
      // 들어올 때 빠르게, 나갈 때 천천히(깜빡이지 않게)
      const a = this.a0[i] * Math.min(1, t * 6) * (1 - t * t);
      const o = i * 16;
      sm[o] = r.x * w; sm[o + 1] = r.y * w; sm[o + 2] = r.z * w; sm[o + 3] = 0;
      sm[o + 4] = u.x * w; sm[o + 5] = u.y * w; sm[o + 6] = u.z * w; sm[o + 7] = 0;
      sm[o + 8] = f.x; sm[o + 9] = f.y; sm[o + 10] = f.z; sm[o + 11] = 0;
      sm[o + 12] = this.px[i]; sm[o + 13] = this.py[i]; sm[o + 14] = this.pz[i]; sm[o + 15] = 1;
      sc[i * 3] = this.cr[i]; sc[i * 3 + 1] = this.cg[i]; sc[i * 3 + 2] = this.cb[i];
      sa[i] = a;
      i++;
    }
    // 머리 위 금빛 기둥: 월드 수직으로 선 가산 띠(멀리서도 스페셜로 읽히게). 점멸 없이 0.4Hz로 아주 천천히 밝기만 오르내린다
    let total = this.n;
    if (pillar) {
      const gs = SPECIAL_RT.goldScale, w = 0.75, h = 3.4, o = total * 16;
      const rl = Math.hypot(r.x, r.z) || 1, rx = (r.x / rl) * w, rz = (r.z / rl) * w;
      sm[o] = rx; sm[o + 1] = 0; sm[o + 2] = rz; sm[o + 3] = 0;
      sm[o + 4] = 0; sm[o + 5] = h; sm[o + 6] = 0; sm[o + 7] = 0;
      sm[o + 8] = f.x; sm[o + 9] = f.y; sm[o + 10] = f.z; sm[o + 11] = 0;
      sm[o + 12] = m.x[mob]; sm[o + 13] = 1.45 * gs + 0.2 + h / 2; sm[o + 14] = m.z[mob]; sm[o + 15] = 1;
      sc[total * 3] = 1; sc[total * 3 + 1] = 0.78; sc[total * 3 + 2] = 0.2;
      sa[total] = (this.reduceFlash ? 0.35 : 0.6) * (0.9 + 0.1 * Math.sin(time * Math.PI * 0.8));
      total++;
    }
    this.sparkMesh.count = total; this.sparkMesh.visible = total > 0;
    this.sparkMesh.instanceMatrix.needsUpdate = true; this.sparkMesh.instanceColor!.needsUpdate = true; this.sparkAlpha.needsUpdate = true;
    this.updateTag(view, camera, width, height, mob, alive);
  }

  /** 챔피언 이름표: 머리 위. 화면 밖이면 숨기고, 위쪽 끝(HUD 띠 높이) 밑으로는 올라가지 않는다 */
  private updateTag(view: GameView, camera: THREE.Camera, width: number, height: number, mob: number, alive: boolean): void {
    const sp = view.special, m = view.mobs;
    const on = alive && sp !== undefined && m.special![mob] === MobSpecial.Champion && m.state[mob] !== MobState.Dying && m.state[mob] !== MobState.Spawning;
    if (!on) { if (this.tagOn) { this.tagOn = false; this.tag.style.display = 'none'; } return; }
    if (sp!.name !== this.tagText) { this.tagText = sp!.name; this.tag.textContent = sp!.name; }
    const h = TARGET_H[m.kind[mob]] * SPECIAL.champion.scale + 0.35;
    this.proj.set(m.x[mob], m.y[mob] + h, m.z[mob]).project(camera);
    const behind = this.proj.z > 1;
    const sx = Math.round((this.proj.x * 0.5 + 0.5) * width), sy = Math.max(14, Math.round((-this.proj.y * 0.5 + 0.5) * height));
    if (behind || sx < -40 || sx > width + 40 || sy > height + 20) { if (this.tagOn) { this.tagOn = false; this.tag.style.display = 'none'; } return; }
    if (!this.tagOn) { this.tagOn = true; this.tag.style.display = 'block'; }
    // 반올림 좌표가 바뀔 때만 style을 쓴다(매 프레임 문자열·DOM 쓰기 없음)
    if (this.tagPos.update(sx, sy)) this.tag.style.transform = 'translate3d(' + sx + 'px,' + sy + 'px,0) translate(-50%,-100%)';
  }

  /** 셰이더 미리 컴파일용 */
  prewarm(on: boolean): void {
    this.auraMesh.count = on ? 1 : 0; this.auraMesh.visible = on;
    this.sparkMesh.count = on ? 1 : 0; this.sparkMesh.visible = on;
  }

  clear(): void {
    this.n = 0; this.trailAcc = 0; this.champAcc = 0;
    this.auraMesh.count = 0; this.auraMesh.visible = false; this.sparkMesh.count = 0; this.sparkMesh.visible = false; this.sack.visible = false;
    this.tagOn = false; this.tag.style.display = 'none';
  }

  dispose(): void {
    this.sack.geometry.dispose(); (this.sack.material as THREE.Material).dispose();
    for (const mesh of [this.auraMesh, this.sparkMesh]) { mesh.geometry.dispose(); (mesh.material as THREE.Material).dispose(); mesh.dispose(); }
    this.tag.remove();
  }
}
